import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/components/ui/Icon';
import { PlaybackRateControl, type PlaybackRate } from '@/components/ui/PlaybackRateControl';
import { useAudioPlayer } from '@/shared/stores/audioPlayer';

/**
 * סרגל נגן הקלטות מתמשך — נעוץ בתחתית המסך מעל כל המערכת. מותקן ב-layout
 * (מחוץ ל-Outlet) כך שההשמעה שורדת סגירת drawer/sidebar וניווט. הוא הבעלים
 * היחיד של אלמנט ה-<audio>. כפתור הסגירה עוצר את ההקלטה (close() מפנה את
 * הטראק → הקומפוננטה יורדת מה-DOM → הדפדפן עוצר את האודיו).
 */
export function PersistentAudioBar() {
  const track = useAudioPlayer((s) => s.track);
  const close = useAudioPlayer((s) => s.close);
  const setStatus = useAudioPlayer((s) => s.setStatus);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [failed, setFailed] = useState(false);
  const [rate, setRate] = useState<PlaybackRate>(1);

  // הסרגל נעוץ בתחתית ומכסה תוכן — מרווח תחתון ל-container הגולל כל עוד הוא
  // פעיל, כדי שאלמנטים בתחתית הדף לא ייחתכו. חובות שנלמדו:
  //   1) לא ל-document.body — body בגובה 100% + box-sizing:border-box,
  //      ו-padding עליו גורם overflow של ה-body. ה-scroll האמיתי ב-#main-content.
  //   2) המרווח = גובה הסרגל **בפועל** (נמדד), לא ערך קבוע — אחרת "רווח לבן".
  //   3) חייב לרוץ בכל render: בניווט #main-content הוא אלמנט חדש, וה-effect
  //      חייב להחיל את המרווח על החדש ולנקות מרווח תקוע ("שטח מת אפור").
  //      לכן — בלי deps, וניקוי ל-'' (לא לערך קודם שאולי כבר תקוע).
  useEffect(() => {
    const main = document.getElementById('main-content');
    if (!main) return;

    if (!track) {
      if (main.style.paddingBottom) main.style.paddingBottom = '';
      return;
    }

    const apply = () => {
      const h = barRef.current?.offsetHeight ?? 0;
      main.style.paddingBottom = h > 0 ? `${h}px` : '';
    };
    apply();

    // גובה הסרגל משתנה (responsive / טקסט ארוך) — עוקבים ומעדכנים.
    const ro = barRef.current ? new ResizeObserver(apply) : null;
    if (barRef.current) ro?.observe(barRef.current);

    return () => {
      ro?.disconnect();
      main.style.paddingBottom = '';
    };
  });

  // טראק חדש → טען, אפס מדדים, ונגן אוטומטית (המשתמש כבר לחץ play במקור).
  useEffect(() => {
    if (!track) return;
    const audio = audioRef.current;
    if (!audio) return;
    setFailed(false);
    setProgress(0);
    setDuration(track.duration ?? 0);
    audio.load();
    audio.play().then(() => setPlaying(true)).catch(() => { setFailed(true); setPlaying(false); });
  }, [track?.src]); // eslint-disable-line react-hooks/exhaustive-deps

  // audio.load() על טראק חדש מאפס את playbackRate — מחילים מחדש על כל
  // החלפת טראק וכל שינוי מהירות, אחרת ההקלטה הבאה מתנגנת ב-×1.
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.playbackRate = rate;
  }, [rate, track?.src]);

  // מצב ההשמעה בפועל נחשף ב-store — כפתור ▶ במסך לא יישאר "מנגן" כשההשמעה נכשלה.
  useEffect(() => { setStatus({ playing, failed }); }, [playing, failed, setStatus]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onError = () => { setFailed(true); setPlaying(false); };
    const onTime = () => setProgress(audio.currentTime);
    const onEnd = () => { setPlaying(false); setProgress(0); };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    // חלק ממקורות ה-WAV/stream מדווחים duration=Infinity עד שהקובץ נטען
    // במלואו. דחיפת currentTime לסוף מכריחה את הדפדפן לפתור את המשך האמיתי.
    const resolveDuration = () => {
      const d = audio.duration;
      if (Number.isFinite(d) && d > 0) {
        setDuration(d);
      } else if (d === Infinity) {
        const onceTime = () => {
          audio.removeEventListener('timeupdate', onceTime);
          if (Number.isFinite(audio.duration)) setDuration(audio.duration);
          audio.currentTime = 0;
        };
        audio.addEventListener('timeupdate', onceTime);
        audio.currentTime = 1e101;
      }
    };

    audio.addEventListener('error', onError);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('loadedmetadata', resolveDuration);
    audio.addEventListener('durationchange', resolveDuration);
    return () => {
      audio.removeEventListener('error', onError);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('loadedmetadata', resolveDuration);
      audio.removeEventListener('durationchange', resolveDuration);
    };
  }, [track?.src]);

  if (!track) return null;

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio || failed) return;
    if (playing) audio.pause();
    else audio.play().then(() => setPlaying(true)).catch(() => { setFailed(true); setPlaying(false); });
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // ב-RTL פס ההתקדמות גדל מימין לשמאל, ולכן ההתחלה היא הקצה הימני
    const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
    const ratio = rtl ? (rect.right - e.clientX) / rect.width : (e.clientX - rect.left) / rect.width;
    audio.currentTime = Math.min(Math.max(ratio, 0), 1) * duration;
  };

  const hasDuration = Number.isFinite(duration) && duration > 0;

  return createPortal(
    <div
      ref={barRef}
      role="region"
      aria-label="נגן הקלטה"
      style={{
        position: 'fixed', insetInline: 0, bottom: 0, zIndex: 1150,
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 16px',
        background: 'var(--surface)', borderTop: '1px solid var(--line)',
        boxShadow: 'var(--shadow-3, 0 -6px 24px rgba(0,0,0,.16))',
      }}
    >
      <audio ref={audioRef} src={track.src} preload="metadata" />

      <button type="button" onClick={toggle} className="btn"
        style={{ width: 40, height: 40, borderRadius: '50%', padding: 0, flexShrink: 0 }}
        aria-label={playing ? 'pause' : 'play'} disabled={failed}>
        <Icon name={playing ? 'pause' : 'play'} size={16} />
      </button>

      <div style={{ minWidth: 0, flexShrink: 0, maxWidth: 220 }}>
        <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {failed ? 'לא ניתן לנגן את ההקלטה' : track.title}
        </div>
        {track.subtitle && (
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {track.subtitle}
          </div>
        )}
      </div>

      <div
        role="slider" aria-valuenow={progress} aria-valuemax={hasDuration ? duration : 0} onClick={seek}
        style={{ flex: 1, minWidth: 60, height: 28, background: 'var(--surface-2)', borderRadius: 'var(--r-2)', position: 'relative', cursor: 'pointer', overflow: 'hidden' }}
      >
        <div style={{ width: hasDuration ? `${(progress / duration) * 100}%` : 0, height: '100%', background: 'var(--accent)', opacity: 0.6, transition: 'width 80ms linear' }} />
      </div>

      <span dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
        {hasDuration ? `${fmt(progress)} / ${fmt(duration)}` : fmt(progress)}
      </span>

      <PlaybackRateControl rate={rate} onChange={setRate} disabled={failed} />

      <button type="button" onClick={close} className="btn ghost"
        style={{ width: 34, height: 34, borderRadius: '50%', padding: 0, flexShrink: 0 }}
        aria-label="סגור נגן" title="עצור וסגור">
        <Icon name="close" size={16} />
      </button>
    </div>,
    document.body,
  );
}

function fmt(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
}
